<script>
  import { format } from 'svelte-i18n';
  import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Tooltip } from '@sveltestrap/sveltestrap';

  import { CodeMirror } from '@client/components/codemirror';
  import { Icon } from '@client/components/icon';
  import { MergeView } from '@client/components/merge-view';

  import { appStore } from '@client/stores/app';

  import { convertHTMLEntities } from '@common/utils/strings';

  export let data = {};
  export let source = '';
  export let fields = [];
  export let selectedUser = {};
  export let recipientEmail = '';
  export let textOnly = false;
  export let originalSource = '';

  let showDiff = false;
  let updatedSource = source;
  let sourceCopy = source;

  let previousEmail = recipientEmail;
  let previousSelectedUser = selectedUser.value;
  let previousFields = JSON.stringify(fields);

  let errors = false;
  let htmlLintErrors = [];
  let liquidLintErrors = [];

  let showBadges = true;

  let collapseUnchanged = true;
  let revertControls = false;

  const onRevertChanges = () => {
    sourceCopy = originalSource;
    updatedSource = originalSource;
  };

  const onReviewClose = () => {
    showDiff = false;
    revertControls = false;
  };

  const onReviewCancel = () => {
    revertControls = false;
    sourceCopy = source;
    updatedSource = source;
  };

  const onModifiedChange = (newValue) => {
    updatedSource = newValue;
  };

  const onReviewApply = async () => {
    source = updatedSource;
    sourceCopy = source;
    showDiff = false;
    revertControls = false;
  };

  const enableChoosingMode = () => {
    revertControls = true;
  };

  $: if (recipientEmail && previousEmail !== recipientEmail) {
    previousEmail = recipientEmail;
  }

  $: if (fields.length && JSON.stringify(fields) !== previousFields) {
    previousFields = JSON.stringify(fields);
  }

  $: if (Object.keys(selectedUser).length && selectedUser.value !== previousSelectedUser) {
    previousSelectedUser = selectedUser.value;
  }

  const formatLintErrors = (messages) =>
    messages
      .map(({ line, message }) => `<div><b>Line ${line || 'n/a'}:</b><div>${convertHTMLEntities(message)}</div></div>`)
      .join('');

  const htmlErrors = ({ detail }) => {
    if (detail.length) {
      htmlLintErrors = formatLintErrors(detail);
    } else {
      htmlLintErrors = [];
    }
  };

  const liquidErrors = ({ detail }) => {
    if (detail.length) {
      liquidLintErrors = formatLintErrors(detail);
    } else {
      liquidLintErrors = [];
    }
  };

  const onToggleDiff = () => {
    showDiff = !showDiff;

    if (showDiff) {
      sourceCopy = source;
      updatedSource = source;
    }
  };

  $: collapseIcon = showBadges ? 'chevron-right' : 'chevron-left';
  $: hideContent = showBadges ? '' : 'hide';
  $: modified = JSON.stringify(originalSource) !== JSON.stringify(source);
  $: errors = htmlLintErrors.length || liquidLintErrors.length;
</script>

<div class="editor" {...$$restProps}>
  <div class="source">
    <div class="source-tour"></div>
    <div class="badges {hideContent}">
      <Icon
        id="collapse-icon"
        class="collapse-icon"
        name={collapseIcon}
        role="button"
        on:click={() => (showBadges = !showBadges)}
      />
      <Tooltip target="collapse-icon" placement="left">
        {showBadges ? $format('label.HIDE_TOOLBAR') : $format('label.SHOW_TOOLBAR')}
      </Tooltip>
      <div class="collapsible-content {hideContent}">
        <div class="validation" class:errors>
          {#if errors}
            <span id="tooltip-errors">
              <Icon name="alert-triangle" />
              {$format('label.ERRORS')}
            </span>
            <Tooltip target="tooltip-errors" placement="top">
              <div class="lint-errors">
                {#if htmlLintErrors.length}
                  <div class="error">
                    <b>{$format('label.HTML_ERRORS')}</b>
                    <div>{@html htmlLintErrors}</div>
                  </div>
                {/if}
                {#if liquidLintErrors.length}
                  <div class="error">
                    <b>{$format('label.LIQUID_ERRORS')}</b>
                    <div>{@html liquidLintErrors}</div>
                  </div>
                {/if}
              </div>
            </Tooltip>
          {:else}
            <Icon name="check" />
            {#if textOnly}
              {$format('label.TEXT').toUpperCase()}
            {:else}
              {$format('label.HTML').toUpperCase()}
            {/if}
          {/if}
        </div>

        {#if modified}
          <div class="actions">
            <Button on:click={onToggleDiff} size="sm" style="flex-shrink: 0;">{$format('label.REVIEW_CHANGES')}</Button>
          </div>
        {/if}
      </div>
    </div>
    <div class="editors">
      <CodeMirror
        bind:value={source}
        lineWrapping={$appStore.mobile}
        bind:data
        on:htmlLintErrors={htmlErrors}
        on:liquidLintErrors={liquidErrors}
        disableFormatting
      />
    </div>
  </div>
</div>

<Modal id="signature-merge-view" isOpen={showDiff} fade={false} size="xl" fullscreen="lg">
  <ModalHeader toggle={onToggleDiff}>
    {@html $format('label.SIGNATURE_EDITOR_MODAL_HEADER', { values: { rulename: data.rulename } })}
  </ModalHeader>
  <ModalBody>
    <div class="merge-view">
      <div class="actions">
        <label for="collapseUnchanged">
          <input id="collapseUnchanged" type="checkbox" bind:checked={collapseUnchanged} />
          {$format('label.COLLAPSE_UNCHANGED')}
        </label>
        {#if revertControls}
          <Button on:click={onRevertChanges} size="sm">{$format('label.UNDO_ALL')}</Button>
        {/if}
      </div>
      <div class="viewer">
        <MergeView
          bind:original={originalSource}
          bind:modified={sourceCopy}
          {onModifiedChange}
          bind:collapseUnchanged
          bind:revertControls
        />
      </div>
    </div>
  </ModalBody>
  <ModalFooter>
    <div class="review-actions">
      {#if revertControls}
        <Button on:click={onReviewCancel} color="link">{$format('label.CANCEL')}</Button>
        <Button on:click={onReviewApply} color="primary">{$format('label.APPLY_CHANGES')}</Button>
      {:else}
        <Button on:click={onReviewClose} color="secondary">{$format('label.DONE_REVIEWING')}</Button>
        <Button color="primary" on:click={enableChoosingMode}>{$format('label.EDIT_CHANGES')}</Button>
      {/if}
    </div>
  </ModalFooter>
</Modal>

<style lang="scss" src="./card-editor.scss"></style>
